// ---- Physics / navigation ----
// The reusable kinematic layer the whole game moves characters through. It has
// two halves, both purely geometric (no THREE dependency):
//
//   1. COLLIDERS — solid footprints that push a character out. Circles (trees,
//      towers) and axis-aligned boxes (walls). Each has an optional vertical
//      extent [minY,maxY] so a collider can be floor-aware: a ground-floor wall
//      blocks you downstairs, a parapet only blocks you upstairs.
//
//   2. SURFACES — things you can stand ON: flat platforms (floors) and ramps
//      (stairs). `surfaceAt` answers "what height is under my feet here?",
//      picking the highest surface I can actually step onto from my current
//      height. This is what makes a real second floor possible: above terrain,
//      stacked, with stairs bridging them.
//
// Decos contribute circle colliders (see world.ts); structures contribute walls,
// floors and stairs (see structure/). Anything new — bridges, ramps, raised
// platforms, dungeons — drops in by emitting the same primitives.

import { heightAt, WATER_LEVEL } from "./terrain.ts";

/** Feet can't go below this ground height — the water's edge. A touch above
 *  WATER_LEVEL so characters stop on the damp bank, never in the water.
 *  Per call, not a module const: WATER_LEVEL is the ACTIVE map's live value
 *  (src/map/active.ts) — a baked const would freeze the pre-spawn default. */
export const shoreStop = (): number => WATER_LEVEL + 0.15;

/** How wide the player is for collision purposes (XZ radius, world units). */
export const PLAYER_RADIUS = 0.45;
/** Player capsule height — used to test which floor-aware colliders apply. */
export const PLAYER_HEIGHT = 1.8;
/** Tallest lip a character can step straight up onto (stairs rise less per step). */
export const STEP_UP = 0.7;

/** A circular solid footprint. Omit minY/maxY for a full-height obstacle.
 *  Optional `id` tags a deco/prop collider so cut/regrow can shrink it in place
 *  (woodcutting stumps). `baseR` is the full-tree radius when tagged. */
export type CircleCollider = {
  x: number; z: number; r: number;
  minY?: number; maxY?: number;
  /** Interactable prop id (scatter `dc<n>` or authored id) when this is a deco footprint. */
  id?: string;
  /** Full radius when `id` is set — restored on regrow after a cut shrinks `r`. */
  baseR?: number;
};
/** An axis-aligned box footprint with optional vertical extent.
 *  Door boxes carry `doorId` + `doorOpen` so open/swinging leaves are walkable
 *  without rebuilding the NavGrid (mutate `doorOpen` in place — same pattern as
 *  `setPropColliderCut` on circle colliders). */
export type BoxCollider = {
  kind: "box";
  minX: number; maxX: number; minZ: number; maxZ: number;
  minY?: number; maxY?: number;
  /** Structure door id (`dr:<placement>:<i>`) when this blocks a closed doorway. */
  doorId?: string;
  /** True while the door is open or swinging — resolve skips this box. */
  doorOpen?: boolean;
};
export type Collider = CircleCollider | BoxCollider;

/** Stump solid radius (matches content/decos/stump.json `radius`). Scaled by
 *  the tree instance's scale at the call site. */
export const STUMP_COLLIDER_R = 0.8;

/** Shrink or restore a prop-tagged circle collider (cut tree → stump solid).
 *  Mutates in place so NavGrid cell lists that hold the same object see it. */
export function setPropColliderCut(
  colliders: Collider[],
  propId: string,
  cut: boolean,
  /** Instance scale (deco scale multiplier). */
  scale = 1,
): void {
  const stumpR = STUMP_COLLIDER_R * scale;
  for (const c of colliders) {
    if (isBox(c)) continue;
    if (c.id !== propId) continue;
    if (cut) c.r = Math.min(stumpR, c.baseR ?? stumpR);
    else c.r = c.baseR ?? c.r;
  }
}

/** Open or close a door-tagged box collider. Mutates in place (NavGrid refs).
 *  Open / swinging → no collision; fully closed → blocks. */
export function setDoorColliderOpen(
  colliders: Collider[],
  doorId: string,
  open: boolean,
): void {
  for (const c of colliders) {
    if (!isBox(c) || c.doorId !== doorId) continue;
    c.doorOpen = open;
  }
}

/** A flat walkable floor at height `y` over an XZ rectangle. */
export type Platform = {
  kind: "platform";
  minX: number; maxX: number; minZ: number; maxZ: number;
  y: number;
  /** Standable PROP top (furniture / deco / wall top — gravity plan): walkable
   *  like any platform, but NEVER an architectural floor — the structure
   *  interior/cutaway storey derivation skips it (a table is not a storey). */
  prop?: boolean;
};
/** A sloped walkable surface (a staircase): height ramps linearly along one axis.
 *  `lowAt` says which end of that axis is the low (yLow) end. */
export type Ramp = {
  kind: "ramp";
  minX: number; maxX: number; minZ: number; maxZ: number;
  yLow: number; yHigh: number;
  axis: "x" | "z";
  lowAt: "min" | "max";
};
export type Surface = Platform | Ramp;

function isBox(c: Collider): c is BoxCollider {
  return (c as BoxCollider).kind === "box";
}

/** Does a collider's vertical extent overlap the player capsule rising from `feetY`? */
function activeAt(c: Collider, feetY: number): boolean {
  const lo = c.minY ?? -Infinity;
  const hi = c.maxY ?? Infinity;
  return feetY + PLAYER_HEIGHT > lo && feetY < hi;
}

/**
 * Push a candidate (x,z) out of every collider it overlaps at height `feetY`,
 * and return the corrected position. Circles shove along the center line; boxes
 * shove out along the nearest face — both let the player slide along an edge
 * rather than stick. Two passes settle being wedged between two obstacles.
 */
export function resolveColliders(
  x: number,
  z: number,
  feetY: number,
  colliders: Collider[],
  selfR: number = PLAYER_RADIUS,
  skip: Collider | null = null,
): { x: number; z: number } {
  for (let pass = 0; pass < 2; pass++) {
    for (const c of colliders) {
      if (c === skip) continue; // don't collide a mover with its own footprint
      if (!activeAt(c, feetY)) continue;
      if (isBox(c)) {
        // Open / swinging doors leave the opening walkable.
        if (c.doorOpen) continue;
        // Closest point on the AABB to the circle center.
        const px = Math.max(c.minX, Math.min(x, c.maxX));
        const pz = Math.max(c.minZ, Math.min(z, c.maxZ));
        const dx = x - px, dz = z - pz;
        const d2 = dx * dx + dz * dz;
        if (d2 >= selfR * selfR) continue; // no overlap
        if (d2 > 1e-8) {
          const d = Math.sqrt(d2);
          const push = (selfR - d) / d;
          x += dx * push; z += dz * push;
        } else {
          // Center inside the box: eject through the nearest face.
          const toMinX = x - c.minX, toMaxX = c.maxX - x;
          const toMinZ = z - c.minZ, toMaxZ = c.maxZ - z;
          const m = Math.min(toMinX, toMaxX, toMinZ, toMaxZ);
          if (m === toMinX) x = c.minX - selfR;
          else if (m === toMaxX) x = c.maxX + selfR;
          else if (m === toMinZ) z = c.minZ - selfR;
          else z = c.maxZ + selfR;
        }
      } else {
        if (c.r <= 0) continue; // disabled / zero-radius (e.g. fully removed solid)
        const dx = x - c.x, dz = z - c.z;
        const min = c.r + selfR;
        const d2 = dx * dx + dz * dz;
        if (d2 >= min * min) continue;
        const d = Math.sqrt(d2);
        if (d < 1e-4) { x = c.x + min; continue; } // dead-center: nudge out along +x
        const push = (min - d) / d;
        x += dx * push; z += dz * push;
      }
    }
  }
  return { x, z };
}

/** Height of a single surface at (x,z), or null if (x,z) is outside its footprint. */
function heightOf(s: Surface, x: number, z: number): number | null {
  if (x < s.minX || x > s.maxX || z < s.minZ || z > s.maxZ) return null;
  if (s.kind === "platform") return s.y;
  const span = s.axis === "x" ? s.maxX - s.minX : s.maxZ - s.minZ;
  let t = span > 1e-6 ? ((s.axis === "x" ? x - s.minX : z - s.minZ) / span) : 0;
  if (s.lowAt === "max") t = 1 - t; // low end is the max side
  return s.yLow + (s.yHigh - s.yLow) * t;
}

/**
 * The height the character stands at when over (x,z), given their current feet
 * height `prevY`. Considers the terrain plus every walkable surface, and returns
 * the HIGHEST one within step-up reach of where they already are — so you climb
 * stairs onto an upper floor, but an upper floor doesn't yank you up from below,
 * and walking off an edge drops you to whatever's beneath.
 */
export function surfaceAt(x: number, z: number, prevY: number, surfaces: Surface[]): number {
  return surfaceAtWith(heightAt, x, z, prevY, surfaces);
}

/** surfaceAt over an EXPLICIT ground function (plan 21: the server's per-map
 *  TerrainCtx passes ITS heightAt; the module-level surfaceAt above passes the
 *  active map's). */
export function surfaceAtWith(
  ground: (x: number, z: number) => number,
  x: number,
  z: number,
  prevY: number,
  surfaces: Surface[],
): number {
  const reach = prevY + STEP_UP;
  let best = -Infinity;
  const terrain = ground(x, z);
  if (terrain <= reach) best = terrain;
  for (const s of surfaces) {
    const h = heightOf(s, x, z);
    if (h === null) continue;
    if (h <= reach && h > best) best = h;
  }
  // Nothing within reach (we're airborne over a hole): fall toward terrain.
  return best === -Infinity ? terrain : best;
}

/**
 * surfaceAtWith, honest: the standable height at (x,z) within step-up reach of
 * `refY`, or NULL when nothing qualifies. Because terrain BELOW a mover is
 * always within reach, null means the terrain surface towers ABOVE refY+STEP_UP
 * with no walkable surface bridging it — a cliff face / the inside of a hill.
 * (surfaceAtWith's legacy fallback returned that unreachable terrain anyway,
 * which is the walk-up-a-vertical-cliff bug; the gravity solver consumes THIS
 * variant instead. See sim/vertical.ts for the grounded/airborne null rules.)
 */
export function standAtWith(
  ground: (x: number, z: number) => number,
  x: number,
  z: number,
  refY: number,
  surfaces: Surface[],
): number | null {
  const reach = refY + STEP_UP;
  let best = -Infinity;
  const terrain = ground(x, z);
  if (terrain <= reach) best = terrain;
  for (const s of surfaces) {
    const h = heightOf(s, x, z);
    if (h === null) continue;
    if (h <= reach && h > best) best = h;
  }
  return best === -Infinity ? null : best;
}

/** standAtWith against the active map's terrain (the surfaceAt sibling). */
export function standAt(x: number, z: number, refY: number, surfaces: Surface[]): number | null {
  return standAtWith(heightAt, x, z, refY, surfaces);
}

/** The highest SURFACE (never terrain) within step-up reach of refY, or null.
 *  The slope-gate exemption test: a staircase/roof ramp built against a steep
 *  face must stay climbable, so movers step onto surfaces regardless of the
 *  terrain gradient underneath them. */
export function surfaceSupportAt(x: number, z: number, refY: number, surfaces: Surface[]): number | null {
  const reach = refY + STEP_UP;
  let best: number | null = null;
  for (const s of surfaces) {
    const h = heightOf(s, x, z);
    if (h === null || h > reach) continue;
    if (best === null || h > best) best = h;
  }
  return best;
}

/**
 * Terrain slope gate: strip the uphill component of a GROUNDED step onto
 * terrain steeper than `maxSlope` (rise/run), so cliff faces and sheer feature
 * walls are real obstacles instead of free ladders. The gradient is sampled at
 * (fromX,fromZ) — the mover's stable prior spot — the clampToShoreWith
 * determinism pattern: pressing straight into a cliff just holds position,
 * with no frame-to-frame bounce. The ±0.4 sample window doubles as a sensing
 * skirt, catching a wall just ahead before the mover reaches it. Terrain-only
 * by design: surfaces (stairs, ramps, roof pitches) are exempt — callers skip
 * the gate when a surface supports the destination.
 */
export function slopeGateWith(
  ground: (x: number, z: number) => number,
  fromX: number,
  fromZ: number,
  x: number,
  z: number,
  maxSlope: number,
): { x: number; z: number } {
  const e = 0.4;
  const gx = (ground(fromX + e, fromZ) - ground(fromX - e, fromZ)) / (2 * e);
  const gz = (ground(fromX, fromZ + e) - ground(fromX, fromZ - e)) / (2 * e);
  const g = Math.hypot(gx, gz);
  if (g <= maxSlope) return { x, z }; // gentle ground — walk freely
  const ux = gx / g, uz = gz / g; // uphill unit direction
  let mvx = x - fromX, mvz = z - fromZ;
  const uphill = mvx * ux + mvz * uz; // > 0 ⇒ the move climbs the steep face
  if (uphill > 0) { mvx -= uphill * ux; mvz -= uphill * uz; } // keep the along-face part
  return { x: fromX + mvx, z: fromZ + mvz };
}

/** slopeGateWith against the active map's terrain. */
export function slopeGate(fromX: number, fromZ: number, x: number, z: number, maxSlope: number): { x: number; z: number } {
  return slopeGateWith(heightAt, fromX, fromZ, x, z, maxSlope);
}

/**
 * AIRBORNE horizontal gate: an arc may not steer into a column whose terrain
 * surface is above the mover's feet (+ step-up grace) — the cliff face. Without
 * it, drifting into a hillside mid-flight would put the mover INSIDE the
 * terrain, where the landing check (standAtWith → null) never fires and they
 * fall through the world. Holds the prior spot, same determinism as the other
 * gates. The step-up grace keeps low-arc uphill jumps working like walking.
 */
export function airWallGateWith(
  ground: (x: number, z: number) => number,
  fromX: number,
  fromZ: number,
  x: number,
  z: number,
  feetY: number,
): { x: number; z: number } {
  return ground(x, z) > feetY + STEP_UP ? { x: fromX, z: fromZ } : { x, z };
}

/** airWallGateWith against the active map's terrain. */
export function airWallGate(fromX: number, fromZ: number, x: number, z: number, feetY: number): { x: number; z: number } {
  return airWallGateWith(heightAt, fromX, fromZ, x, z, feetY);
}

/** Is the ground at (x,z) below the walkable shoreline (i.e. underwater)? */
export function inWater(x: number, z: number): boolean {
  return heightAt(x, z) < shoreStop();
}

/** True when a walkable surface (a pier deck, bridge, or floor) covers (x,z)
 *  within step-up reach of `prevY` AND sits above the waterline — i.e. the mover
 *  is standing ON a deck OVER the water, not wading in. The player's move paths
 *  (client game.ts + server movement handler) consult this to SKIP the shore
 *  clamp there, so you can walk out onto a fishing dock; plain terrain-only deep
 *  water still blocks a wading step normally. Scoped to the player — NPCs keep
 *  the flat water block (they never path onto piers). */
export function onDeckOverWater(x: number, z: number, prevY: number, surfaces: Surface[]): boolean {
  return onDeckOverWaterWith(WATER_LEVEL, x, z, prevY, surfaces);
}

/** onDeckOverWater over an EXPLICIT water level (the multi-map server passes its
 *  MapRt's `terrain.waterLevel`, mirroring clampToShoreWith). */
export function onDeckOverWaterWith(
  waterLevel: number,
  x: number,
  z: number,
  prevY: number,
  surfaces: Surface[],
): boolean {
  const reach = prevY + STEP_UP;
  const stop = waterLevel + 0.15; // same shoreline rule clampToShore uses
  for (const s of surfaces) {
    const h = heightOf(s, x, z);
    if (h !== null && h <= reach && h > stop) return true;
  }
  return false;
}

/**
 * True when terrain under (x,z) is wet AND the mover is NOT standing on a dry
 * deck above the waterline. This is the player "feet in water" rule — wade
 * slow, wade clamp, splash FX, water soft-landing — as opposed to terrain-only
 * `inWater` (fish cast, scatter lint, NPC water blocks) which ignores decks.
 *
 * The fishing-dock bug: terrain under a pier is deep water, so bare `inWater`
 * was true while the player stood on dry planks → full wade slow. Shore clamp
 * already used `onDeckOverWater`; wading speed did not.
 */
export function isWading(x: number, z: number, feetY: number, surfaces: Surface[]): boolean {
  return isWadingWith(heightAt, WATER_LEVEL, x, z, feetY, surfaces);
}

/** isWading over an EXPLICIT ground + water level (multi-map server). */
export function isWadingWith(
  ground: (x: number, z: number) => number,
  waterLevel: number,
  x: number,
  z: number,
  feetY: number,
  surfaces: Surface[],
): boolean {
  const stop = waterLevel + 0.15;
  if (ground(x, z) >= stop) return false;
  return !onDeckOverWaterWith(waterLevel, x, z, feetY, surfaces);
}

/**
 * Keep a mover out of the water. If the candidate (x,z) is underwater, slide
 * along the shore instead of stepping in: drop the part of the move heading into
 * the water (down the terrain gradient) and keep the along-shore part. The
 * gradient is sampled at `fromX,fromZ` (their stable, dry prior spot) so the
 * result is deterministic — pressing straight into the bank just holds position,
 * with no frame-to-frame bounce. Falls back to the dry spot if it can't slide to
 * land (flat bottom / tight cove). Returns a point at/above shoreStop().
 */
export function clampToShore(
  fromX: number,
  fromZ: number,
  x: number,
  z: number,
): { x: number; z: number } {
  return clampToShoreWith(heightAt, WATER_LEVEL, fromX, fromZ, x, z);
}

/** clampToShore over an EXPLICIT ground function + water level (plan 21: the
 *  server's per-map TerrainCtx passes ITS pair). */
export function clampToShoreWith(
  ground: (x: number, z: number) => number,
  waterLevel: number,
  fromX: number,
  fromZ: number,
  x: number,
  z: number,
): { x: number; z: number } {
  const stop = waterLevel + 0.15; // the shoreStop() rule against the passed level
  const wet = (wx: number, wz: number) => ground(wx, wz) < stop;
  if (!wet(x, z)) return { x, z };
  const e = 0.4;
  // Uphill (toward land) direction at the prior spot.
  let nx = ground(fromX + e, fromZ) - ground(fromX - e, fromZ);
  let nz = ground(fromX, fromZ + e) - ground(fromX, fromZ - e);
  const g = Math.hypot(nx, nz);
  if (g > 1e-4) {
    nx /= g; nz /= g;
    let mvx = x - fromX, mvz = z - fromZ;
    const into = mvx * nx + mvz * nz; // < 0 ⇒ the move heads into the water
    if (into < 0) { mvx -= into * nx; mvz -= into * nz; } // strip the into-water part
    const sx = fromX + mvx, sz = fromZ + mvz;
    if (!wet(sx, sz)) return { x: sx, z: sz };
  }
  // Can't slide to dry land: hold the dry spot (stable — no jitter).
  return { x: fromX, z: fromZ };
}

/**
 * The WADING clamp — clampToShore inverted for a mover already IN the water
 * (a gravity landing put them there; walking never does). From a wet spot,
 * the along-shore and toward-land parts of a move survive but the into-deeper-
 * water component is stripped, so a landed swimmer can only wade back out.
 * Same from-spot gradient sampling as clampToShoreWith (deterministic, no
 * jitter); a flat bottom (no gradient) moves freely — better to let the mover
 * hunt for the bank than pin them mid-lake.
 */
export function wadeClampWith(
  ground: (x: number, z: number) => number,
  waterLevel: number,
  fromX: number,
  fromZ: number,
  x: number,
  z: number,
): { x: number; z: number } {
  const stop = waterLevel + 0.15;
  if (ground(fromX, fromZ) >= stop) return { x, z }; // not wading — not our clamp
  const e = 0.4;
  let nx = ground(fromX + e, fromZ) - ground(fromX - e, fromZ);
  let nz = ground(fromX, fromZ + e) - ground(fromX, fromZ - e);
  const g = Math.hypot(nx, nz);
  if (g <= 1e-4) return { x, z }; // flat bottom: free to move (find the bank)
  nx /= g; nz /= g; // uphill = toward land
  let mvx = x - fromX, mvz = z - fromZ;
  const into = mvx * nx + mvz * nz; // < 0 ⇒ heading deeper
  if (into < 0) { mvx -= into * nx; mvz -= into * nz; }
  return { x: fromX + mvx, z: fromZ + mvz };
}

/** wadeClampWith against the active map's terrain + water level. */
export function wadeClamp(fromX: number, fromZ: number, x: number, z: number): { x: number; z: number } {
  return wadeClampWith(heightAt, WATER_LEVEL, fromX, fromZ, x, z);
}

// --- Static-collider grid (Phase C of knowledge-base/plans/02) ----------------------------
// The world holds thousands of static deco/structure colliders; brute-forcing
// them per mover per frame scales with WORLD size, not with what's nearby. So
// makeNavGrid buckets every collider it's given into a coarse uniform grid
// (each collider lands in every cell its bbox touches), and `resolve` tests
// only the 3×3 cells around the mover. Colliders pushed into `colliders`
// AFTER construction (the NPCs' — they move every frame) form a small dynamic
// tail that is always scanned brute-force; `staticCount` marks the split.
//
// A FACTORY since plan 21: the multi-map server builds one NavGrid per MapRt.
// The module-level navWorld/setWorldNav/navResolve below wrap a default
// instance — the single-map client (and physics.test.ts) are unchanged.
const NAV_CELL = 30; // ≫ any collider-interaction range (selfR + max deco r ≈ 4)

function cellKey(cx: number, cz: number): number {
  return (cx + 2048) * 4096 + (cz + 2048);
}

export type NavGrid = ReturnType<typeof makeNavGrid>;

/** One map's collider grid + surfaces. `colliders`/`surfaces` are the SAME
 *  array identities passed in — push dynamic colliders (NPCs) onto `colliders`
 *  after construction and they form the always-scanned dynamic tail. */
export function makeNavGrid(colliders: Collider[], surfaces: Surface[]) {
  const navCells = new Map<number, Collider[]>();
  const staticCount = colliders.length;

  function insertStatic(c: Collider): void {
    const minX = isBox(c) ? c.minX : c.x - c.r;
    const maxX = isBox(c) ? c.maxX : c.x + c.r;
    const minZ = isBox(c) ? c.minZ : c.z - c.r;
    const maxZ = isBox(c) ? c.maxZ : c.z + c.r;
    const x0 = Math.floor(minX / NAV_CELL), x1 = Math.floor(maxX / NAV_CELL);
    const z0 = Math.floor(minZ / NAV_CELL), z1 = Math.floor(maxZ / NAV_CELL);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const k = cellKey(cx, cz);
        let arr = navCells.get(k);
        if (!arr) navCells.set(k, (arr = []));
        arr.push(c);
      }
    }
  }
  for (const c of colliders) insertStatic(c);

  // Surfaces bucket the same way (standable-geometry growth: furniture tops,
  // roofs, wall tops, deco tops push the flat list into the thousands — the
  // per-frame standing-height scan must stay O(nearby)). Surfaces pushed after
  // construction form the same always-scanned dynamic tail as colliders.
  const surfCells = new Map<number, Surface[]>();
  const staticSurfCount = surfaces.length;

  function insertSurf(s: Surface): void {
    const x0 = Math.floor(s.minX / NAV_CELL), x1 = Math.floor(s.maxX / NAV_CELL);
    const z0 = Math.floor(s.minZ / NAV_CELL), z1 = Math.floor(s.maxZ / NAV_CELL);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const k = cellKey(cx, cz);
        let arr = surfCells.get(k);
        if (!arr) surfCells.set(k, (arr = []));
        arr.push(s);
      }
    }
  }
  for (const s of surfaces) insertSurf(s);

  /** Surfaces whose footprint could cover (x,z) — the mover's 3×3 cell
   *  neighbourhood plus the dynamic tail. Returns a REUSED scratch array:
   *  consume synchronously, never retain. Duplicates (a surface spanning
   *  several queried cells) are harmless to the max-scan consumers. */
  function surfacesNear(x: number, z: number): Surface[] {
    _sCand.length = 0;
    const cx = Math.floor(x / NAV_CELL), cz = Math.floor(z / NAV_CELL);
    for (let ix = cx - 1; ix <= cx + 1; ix++) {
      for (let iz = cz - 1; iz <= cz + 1; iz++) {
        const arr = surfCells.get(cellKey(ix, iz));
        if (arr) for (const s of arr) _sCand.push(s);
      }
    }
    for (let i = staticSurfCount; i < surfaces.length; i++) _sCand.push(surfaces[i]!);
    return _sCand;
  }

  /** standAtWith, grid-gated: same result as scanning the full surface list. */
  function standAt(
    ground: (x: number, z: number) => number,
    x: number,
    z: number,
    refY: number,
  ): number | null {
    return standAtWith(ground, x, z, refY, surfacesNear(x, z));
  }

  /** surfaceAtWith (legacy fallback semantics), grid-gated. */
  function surfaceAt(
    ground: (x: number, z: number) => number,
    x: number,
    z: number,
    prevY: number,
  ): number {
    return surfaceAtWith(ground, x, z, prevY, surfacesNear(x, z));
  }

  /**
   * resolveColliders, grid-gated: only the static colliders in the mover's 3×3
   * cell neighbourhood plus the (small, moving) dynamic tail are tested. A
   * multi-cell collider can appear in several queried cells — the duplicate
   * push-out test is harmless. Same result as passing the full collider list,
   * at O(nearby) instead of O(world).
   */
  function resolve(
    x: number,
    z: number,
    feetY: number,
    selfR: number = PLAYER_RADIUS,
    skip: Collider | null = null,
  ): { x: number; z: number } {
    _cand.length = 0;
    const cx = Math.floor(x / NAV_CELL), cz = Math.floor(z / NAV_CELL);
    for (let ix = cx - 1; ix <= cx + 1; ix++) {
      for (let iz = cz - 1; iz <= cz + 1; iz++) {
        const arr = navCells.get(cellKey(ix, iz));
        if (arr) for (const c of arr) _cand.push(c);
      }
    }
    for (let i = staticCount; i < colliders.length; i++) _cand.push(colliders[i]!);
    return resolveColliders(x, z, feetY, _cand, selfR, skip);
  }

  return { colliders, surfaces, resolve, surfacesNear, standAt, surfaceAt };
}

// Reused candidate scratch — resolve is called per mover per frame. Shared
// across NavGrid instances (single-threaded, consumed synchronously).
const _cand: Collider[] = [];
const _sCand: Surface[] = [];

// --- Shared world nav singleton (the DEFAULT NavGrid) -----------------------
// Built once by world.ts and read by anything that needs to resolve a height
// without threading the surface list through every call (e.g. remote players).
// The multi-map server ignores this after boot — each MapRt holds its own
// NavGrid (src/server/maps.ts).
export const navWorld: { colliders: Collider[]; surfaces: Surface[] } = {
  colliders: [],
  surfaces: [],
};
let defaultGrid: NavGrid | null = null;

export function setWorldNav(colliders: Collider[], surfaces: Surface[]): void {
  navWorld.colliders = colliders;
  navWorld.surfaces = surfaces;
  defaultGrid = makeNavGrid(colliders, surfaces);
}

/** Grid-gated resolve against the default navWorld (see makeNavGrid). */
export function navResolve(
  x: number,
  z: number,
  feetY: number,
  selfR: number = PLAYER_RADIUS,
  skip: Collider | null = null,
): { x: number; z: number } {
  if (!defaultGrid) return { x, z }; // no world built yet (pre-spawn client frames)
  return defaultGrid.resolve(x, z, feetY, selfR, skip);
}

/** Grid-gated standAt against the default navWorld + the active map's terrain.
 *  Pre-world frames fall back to the (empty) flat list — terrain-only. */
export function navStandAt(x: number, z: number, refY: number): number | null {
  if (defaultGrid) return defaultGrid.standAt(heightAt, x, z, refY);
  return standAtWith(heightAt, x, z, refY, navWorld.surfaces);
}

/** Grid-gated legacy surfaceAt against the default navWorld (same fallback
 *  semantics as surfaceAt over the flat list — kept for non-solver callers). */
export function navSurfaceAt(x: number, z: number, prevY: number): number {
  if (defaultGrid) return defaultGrid.surfaceAt(heightAt, x, z, prevY);
  return surfaceAtWith(heightAt, x, z, prevY, navWorld.surfaces);
}

/** Nearby-surface query against the default navWorld (reused scratch — consume
 *  synchronously). Pre-world: the full (usually empty) flat list. */
export function navSurfacesNear(x: number, z: number): Surface[] {
  return defaultGrid ? defaultGrid.surfacesNear(x, z) : navWorld.surfaces;
}

/** Grid-gated surfaceSupportAt against the default navWorld. */
export function navSurfaceSupport(x: number, z: number, refY: number): number | null {
  return surfaceSupportAt(x, z, refY, navSurfacesNear(x, z));
}
